import React, { useState, useEffect } from 'react';
import { getReceipts, createReceipt, getReceiptDetails } from '../../services/api';
import api from '../../services/api';
import { ClipboardList, Search, Clock, Package, CheckCircle, Plus, Eye, Truck, Calendar, Hash, Box, X, AlertTriangle, ShieldCheck, Archive } from 'lucide-react';

const ClientReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    supplier_name: '',
    expected_date: '',
    shipping_date: '',
    estimated_arrival_date: '',
    carrier: '',
    tracking_number: '',
    packaging_type: 'colis',
    packaging_count: '',
    notes: '',
    products: []
  });

  // Details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    loadData();
    fetchProducts();
  }, []);

  const loadData = async () => {
    try {
      const response = await getReceipts({});
      setReceipts(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
  };

  const handleCreateReceipt = async (e) => {
    e.preventDefault();
    if (createFormData.products.length === 0) {
      alert('Veuillez ajouter au moins un produit');
      return;
    }
    try {
      const payload = {
        ...createFormData,
        packaging_count: createFormData.packaging_count ? parseInt(createFormData.packaging_count) : null
      };
      await createReceipt(payload);
      alert('Réception créée avec succès !');
      setShowCreateModal(false);
      loadData();
      setCreateFormData({
        supplier_name: '', expected_date: '', shipping_date: '',
        estimated_arrival_date: '', carrier: '', tracking_number: '',
        packaging_type: 'colis', packaging_count: '', notes: '', products: []
      });
    } catch (error) {
      console.error('Erreur création réception:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const addProductToReceipt = () => {
    setCreateFormData(prev => ({
      ...prev,
      products: [...prev.products, { product_id: '', expected_quantity: 1, lot_number: '', expiry_date: '' }]
    }));
  };

  const removeProductFromReceipt = (index) => {
    setCreateFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const updateReceiptProduct = (index, field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      products: prev.products.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const handleViewDetails = async (receiptId) => {
    try {
      const response = await getReceiptDetails(receiptId);
      setReceiptDetails(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const handleCancelReceipt = async (receiptId) => {
    if (!cancelReason.trim()) {
      alert('Veuillez saisir un motif d\'annulation');
      return;
    }
    try {
      await api.patch(`/receipts/${receiptId}/status`, { status: 'cancelled', reason: cancelReason });
      setShowCancelForm(false);
      setCancelReason('');
      loadData();
      const res = await getReceiptDetails(receiptId);
      setReceiptDetails(res.data);
    } catch (error) {
      alert('Erreur : ' + (error.response?.data?.detail || error.message));
    }
  };

  const statusConfig = {
    planned: { label: 'Planifiée', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
    in_transit: { label: 'En livraison', icon: Truck, color: 'text-blue-600', bg: 'bg-blue-100' },
    delivered: { label: 'Livrée', icon: Package, color: 'text-violet-600', bg: 'bg-violet-100' },
    inspected: { label: 'Contrôlée', icon: ShieldCheck, color: 'text-cyan-600', bg: 'bg-cyan-100' },
    stocked: { label: 'Mise en stock', icon: Archive, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    sav: { label: 'SAV', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
    cancelled: { label: 'Annulé', icon: X, color: 'text-gray-600', bg: 'bg-gray-100' },
  };

  const filtered = (filterStatus === 'all' ? receipts : receipts.filter(r => r.status === filterStatus))
    .filter(r =>
      r.receipt_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.supplier_name && r.supplier_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.carrier && r.carrier.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.tracking_number && r.tracking_number.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  if (loading) {
    return <div className="flex items-center justify-center min-h-96 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="client-receipts">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Mes Réceptions</h1>
          <p className="text-slate-500">{receipts.length} réceptions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600"
        >
          <Plus size={20} />
          Nouvelle Réception
        </button>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Object.entries(statusConfig).map(([key, config]) => {
          const count = receipts.filter(r => r.status === key).length;
          const Icon = config.icon;
          return (
            <div key={key} className={`${config.bg} p-4 rounded-xl flex items-center gap-3`}>
              <Icon size={24} className={config.color} />
              <div>
                <div className={`text-2xl font-bold ${config.color}`}>{count}</div>
                <div className="text-xs text-slate-600">{config.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter + Search */}
      <div className="flex flex-wrap gap-3 mb-6">
        {['all', 'planned', 'in_transit', 'delivered', 'inspected', 'stocked', 'sav'].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filterStatus === s
                ? 'bg-blue-500 text-white'
                : 'bg-white text-slate-500 border border-slate-200'
            }`}
          >
            {s === 'all' ? 'Toutes' : (statusConfig[s]?.label || s)}
          </button>
        ))}
        <div className="flex-1 min-w-60 flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-slate-200">
          <Search size={18} className="text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher par n°, fournisseur, transporteur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-sm"
          />
        </div>
      </div>

      {/* Receipt cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(receipt => {
          const config = statusConfig[receipt.status] || statusConfig.planned;
          const Icon = config.icon;
          return (
            <div key={receipt.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex justify-between items-start">
                <div>
                  <div className="font-semibold text-slate-800">{receipt.receipt_number}</div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <Truck size={14} /> {receipt.supplier_name || 'N/A'}
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${config.bg} ${config.color}`}>
                  <Icon size={14} />
                  {config.label}
                </div>
              </div>
              <div className="p-4 space-y-2">
                {receipt.expected_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Date attendue</span>
                    <span className="font-medium text-slate-800">
                      {new Date(receipt.expected_date).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
                {receipt.shipping_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Date d'envoi</span>
                    <span className="font-medium text-slate-800">
                      {new Date(receipt.shipping_date).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
                {receipt.carrier && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Transporteur</span>
                    <span className="font-medium text-slate-800">{receipt.carrier}</span>
                  </div>
                )}
                {receipt.tracking_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">N° de suivi</span>
                    <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{receipt.tracking_number}</span>
                  </div>
                )}
                {receipt.packaging_count > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Colisage</span>
                    <span className="font-medium text-slate-800">{receipt.packaging_count} {receipt.packaging_type || 'colis'}</span>
                  </div>
                )}
                {receipt.received_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Reçue le</span>
                    <span className="font-medium text-emerald-600">
                      {new Date(receipt.received_date).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-100">
                <button
                  onClick={() => handleViewDetails(receipt.id)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-50 text-blue-500 rounded-lg text-sm font-medium hover:bg-slate-100"
                >
                  <Eye size={16} />
                  Voir Détails
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center">
          <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Aucune réception trouvée</p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-slate-200 z-10">
              <h2 className="text-xl font-bold text-slate-800">Nouvelle Réception</h2>
              <button onClick={() => setShowCreateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateReceipt} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur *</label>
                <input
                  type="text"
                  value={createFormData.supplier_name}
                  onChange={e => setCreateFormData({...createFormData, supplier_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom du fournisseur"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date d'envoi</label>
                  <input
                    type="date"
                    value={createFormData.shipping_date}
                    onChange={e => setCreateFormData({...createFormData, shipping_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date d'arrivée estimée</label>
                  <input
                    type="date"
                    value={createFormData.estimated_arrival_date}
                    onChange={e => setCreateFormData({...createFormData, estimated_arrival_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date attendue</label>
                  <input
                    type="date"
                    value={createFormData.expected_date}
                    onChange={e => setCreateFormData({...createFormData, expected_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Transporteur</label>
                  <input
                    type="text"
                    value={createFormData.carrier}
                    onChange={e => setCreateFormData({...createFormData, carrier: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom du transporteur"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">N° de suivi</label>
                  <input
                    type="text"
                    value={createFormData.tracking_number}
                    onChange={e => setCreateFormData({...createFormData, tracking_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Numéro de suivi"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Colisage</label>
                    <select
                      value={createFormData.packaging_type}
                      onChange={e => setCreateFormData({...createFormData, packaging_type: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="colis">Colis</option>
                      <option value="palettes">Palettes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                    <input
                      type="number"
                      value={createFormData.packaging_count}
                      onChange={e => setCreateFormData({...createFormData, packaging_count: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Products section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-slate-800">Produits *</h3>
                  <button
                    type="button"
                    onClick={addProductToReceipt}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    <Plus size={16} />
                    Ajouter
                  </button>
                </div>

                {createFormData.products.length === 0 && (
                  <p className="text-sm text-slate-400 italic">Cliquez sur "Ajouter" pour ajouter des produits</p>
                )}

                {createFormData.products.map((product, index) => (
                  <div key={index} className="bg-slate-50 rounded-lg p-3 mb-2 space-y-2">
                    <div className="flex gap-2 items-center">
                      <select
                        value={product.product_id}
                        onChange={e => updateReceiptProduct(index, 'product_id', e.target.value)}
                        className="flex-[2] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Sélectionner un produit</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={product.expected_quantity}
                        onChange={e => updateReceiptProduct(index, 'expected_quantity', parseInt(e.target.value) || 1)}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        placeholder="Qté"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => removeProductFromReceipt(index)}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={product.lot_number}
                        onChange={e => updateReceiptProduct(index, 'lot_number', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="N° de lot (optionnel)"
                      />
                      <input
                        type="date"
                        value={product.expiry_date}
                        onChange={e => updateReceiptProduct(index, 'expiry_date', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={createFormData.notes}
                  onChange={e => setCreateFormData({...createFormData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Notes additionnelles..."
                />
              </div>

              <div className="flex gap-3 justify-end pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-500 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                >
                  Créer la Réception
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && receiptDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailsModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-slate-200 z-10">
              <h2 className="text-xl font-bold text-slate-800">
                Détails - {receiptDetails.receipt.receipt_number}
              </h2>
              <button onClick={() => setShowDetailsModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Truck size={18} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500">Fournisseur</div>
                    <div className="text-sm font-medium text-slate-800">{receiptDetails.receipt.supplier_name}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500">Statut</div>
                    <div className="text-sm font-medium text-slate-800">
                      {statusConfig[receiptDetails.receipt.status]?.label || receiptDetails.receipt.status}
                    </div>
                  </div>
                </div>
                {receiptDetails.receipt.expected_date && (
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Date attendue</div>
                      <div className="text-sm font-medium text-slate-800">{new Date(receiptDetails.receipt.expected_date).toLocaleDateString('fr-FR')}</div>
                    </div>
                  </div>
                )}
                {receiptDetails.receipt.shipping_date && (
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-blue-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Date d'envoi</div>
                      <div className="text-sm font-medium text-slate-800">{new Date(receiptDetails.receipt.shipping_date).toLocaleDateString('fr-FR')}</div>
                    </div>
                  </div>
                )}
                {receiptDetails.receipt.estimated_arrival_date && (
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-amber-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Arrivée estimée</div>
                      <div className="text-sm font-medium text-slate-800">{new Date(receiptDetails.receipt.estimated_arrival_date).toLocaleDateString('fr-FR')}</div>
                    </div>
                  </div>
                )}
                {receiptDetails.receipt.carrier && (
                  <div className="flex items-start gap-3">
                    <Truck size={18} className="text-blue-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Transporteur</div>
                      <div className="text-sm font-medium text-slate-800">{receiptDetails.receipt.carrier}</div>
                    </div>
                  </div>
                )}
                {receiptDetails.receipt.tracking_number && (
                  <div className="flex items-start gap-3">
                    <Hash size={18} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">N° de suivi</div>
                      <div className="text-sm font-medium text-slate-800">{receiptDetails.receipt.tracking_number}</div>
                    </div>
                  </div>
                )}
                {receiptDetails.receipt.packaging_count > 0 && (
                  <div className="flex items-start gap-3">
                    <Box size={18} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Colisage</div>
                      <div className="text-sm font-medium text-slate-800">{receiptDetails.receipt.packaging_count} {receiptDetails.receipt.packaging_type || 'colis'}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Package size={18} className="text-slate-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-slate-500">Produits attendus</div>
                    <div className="text-sm font-medium text-slate-800">{receiptDetails.total_products} unités</div>
                  </div>
                </div>
              </div>

              {receiptDetails.receipt.notes && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Notes</div>
                  <p className="text-sm text-slate-700">{receiptDetails.receipt.notes}</p>
                </div>
              )}

              {/* Suivi des statuts */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b-2 border-slate-200">Suivi des Statuts</h3>
                <div className="space-y-0">
                  {[
                    { key: 'created_at', label: 'Réception créée', activeColor: 'bg-slate-500' },
                    { key: 'in_transit_date', label: 'En livraison', activeColor: 'bg-blue-500' },
                    { key: 'delivered_date', label: 'Livrée', activeColor: 'bg-violet-500' },
                    { key: 'inspected_date', label: 'Contrôlée', activeColor: 'bg-cyan-500' },
                    { key: 'stocked_date', label: 'Mise en stock', activeColor: 'bg-emerald-500' },
                    { key: 'sav_date', label: 'SAV', activeColor: 'bg-red-500' },
                    { key: 'cancelled_date', label: 'Annulé', activeColor: 'bg-gray-500' },
                  ].map(step => {
                    const date = receiptDetails.receipt[step.key];
                    return (
                      <div key={step.key} className="flex items-start gap-3 py-2 border-l-2 border-slate-200 ml-2 pl-4 relative">
                        <div className={`w-3 h-3 rounded-full absolute -left-[7px] top-3 border-2 border-white shadow-sm ${date ? step.activeColor : 'bg-slate-200'}`} />
                        <div className="flex-1">
                          <div className={`text-sm ${date ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                            {step.label}
                          </div>
                          <div className="text-xs text-slate-500">
                            {date ? new Date(date).toLocaleString('fr-FR') : '—'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Motif d'annulation */}
              {receiptDetails.receipt.cancellation_reason && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-500 mb-2 text-sm">Motif d'annulation</h3>
                  <p className="text-gray-700 text-sm m-0">{receiptDetails.receipt.cancellation_reason}</p>
                  {receiptDetails.receipt.cancelled_by && (
                    <p className="text-gray-400 text-xs mt-2">Par : {receiptDetails.receipt.cancelled_by}</p>
                  )}
                </div>
              )}

              {/* Bouton annuler */}
              {receiptDetails.receipt.status !== 'cancelled' && receiptDetails.receipt.status !== 'stocked' && (
                <div>
                  {!showCancelForm ? (
                    <button
                      onClick={() => setShowCancelForm(true)}
                      className="px-5 py-2.5 bg-red-50 text-red-600 border border-red-300 rounded-lg font-semibold text-sm hover:bg-red-100"
                    >
                      Annuler cette réception
                    </button>
                  ) : (
                    <div className="border border-red-300 rounded-xl p-4 bg-red-50">
                      <h4 className="text-red-600 font-semibold text-sm mb-3">Motif d'annulation (obligatoire)</h4>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Saisissez le motif d'annulation..."
                        className="w-full min-h-[80px] p-2.5 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleCancelReceipt(receiptDetails.receipt.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700"
                        >
                          Confirmer l'annulation
                        </button>
                        <button
                          onClick={() => { setShowCancelForm(false); setCancelReason(''); }}
                          className="px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-lg font-medium text-sm hover:bg-gray-50"
                        >
                          Retour
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Products table */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b-2 border-slate-200">Produits</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Produit</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">SKU</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Qté Attendue</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Qté Reçue</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">N° Lot</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiptDetails.lines.map((line, index) => (
                        <tr key={index} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="px-3 py-2 text-sm font-medium text-slate-800">{line.product?.name || 'N/A'}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{line.product?.sku || 'N/A'}</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600">{line.expected_quantity}</td>
                          <td className="px-3 py-2 text-sm font-semibold text-slate-800">{line.received_quantity}</td>
                          <td className="px-3 py-2 text-sm text-slate-500">{line.lot_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReceipts;
